// Job contacts: the UI edits a flat list [{id, position, name, phone, email}]; storage stays grouped by
// position key ({[key]: {label, name, phone, email, additional: [...]}}) so downstream code keeps working.
export const STANDARD_POSITIONS = [
  { key: "estimator", label: "Estimator" },
  { key: "superintendent", label: "Superintendent" },
  { key: "project_manager", label: "Project Manager" },
  { key: "billing", label: "Billing" },
  { key: "certified_payroll", label: "Certified Payroll" },
];

const STANDARD_BY_KEY = Object.fromEntries(STANDARD_POSITIONS.map((p) => [p.key, p.label]));
const STANDARD_BY_LABEL = Object.fromEntries(STANDARD_POSITIONS.map((p) => [p.label.toLowerCase(), p.key]));

export const positionKey = (label) => {
  const l = (label || "").trim();
  return STANDARD_BY_LABEL[l.toLowerCase()] || l.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "other";
};

export const positionLabel = (key, entry) =>
  STANDARD_BY_KEY[key] || entry?.label || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const hasInfo = (c) => !!(c && ((c.name || "").trim() || (c.phone || "").trim() || (c.email || "").trim()));

const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export const emptyContact = (position = "", company = "") => ({ id: newId(), position, name: "", company, phone: "", email: "" });

export const contactsToList = (contacts) => {
  const out = [];
  const keys = [...STANDARD_POSITIONS.map((p) => p.key), ...Object.keys(contacts || {}).filter((k) => !STANDARD_BY_KEY[k])];
  for (const key of keys) {
    const entry = (contacts || {})[key];
    if (!entry) continue;
    const label = positionLabel(key, entry);
    [entry, ...(entry.additional || [])].filter(hasInfo).forEach((p) =>
      out.push({ id: newId(), position: label, name: p.name || "", company: p.company || "", phone: p.phone || "", email: p.email || "" })
    );
  }
  return out;
};

export const positionEmails = (contacts, key) => {
  const entry = (contacts || {})[key];
  if (!entry) return [];
  const seen = new Set();
  return [entry, ...(entry.additional || [])]
    .map((p) => (p?.email || "").trim())
    .filter((e) => e.includes("@") && !seen.has(e.toLowerCase()) && seen.add(e.toLowerCase()));
};

export const buildPayAppMailto = (job, billing, emails) => {
  const amount = Number(billing.current_payment_due || billing.amount || 0);
  const amountStr = amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
  const label = job.gc_billing_form ? "Billing" : "Pay Application";
  const subject = `${label} #${billing.billing_number} – ${job.name}${job.project_number ? ` (Project #${job.project_number})` : ""}`;
  const body = `Hello,\n\nAttached is ${label} #${billing.billing_number} for ${job.name} in the amount of ${amountStr}.\n\nPlease let us know if you have any questions.\n\nThank you,\nV2R Masonry`;
  return buildMailto(emails, subject, body);
};

export const buildCertifiedPayrollMailto = (job, payroll, emails) => {
  const projectNo = job.certified_payroll?.project_no || job.project_number;
  const subject = `Certified Payroll #${payroll.payroll_no} (WE ${payroll.week_ending}) – ${job.name}${projectNo ? ` (Project #${projectNo})` : ""}`;
  const detail = payroll.no_work ? "No work was performed this week." : payroll.is_final ? "This is the final payroll for the project." : "";
  const body = `Hello,\n\nAttached is Certified Payroll #${payroll.payroll_no} (WH-347) for ${job.name}, week ending ${payroll.week_ending}.${detail ? ` ${detail}` : ""}\n\nPlease let us know if you have any questions.\n\nThank you,\nV2R Masonry`;
  return buildMailto(emails, subject, body);
};

const buildMailto = (emails, subject, body) =>
  `mailto:${emails.join(",")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

// Opens the default mail client; anchor click (not location.href) so the SPA stays put and tests can observe it.
export const openMailto = (href, testId = "mailto-link") => {
  const a = document.createElement("a");
  a.href = href;
  a.setAttribute("data-testid", testId);
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const listToContacts = (list) => {
  const grouped = {};
  for (const c of list || []) {
    if (!hasInfo(c)) continue;
    const key = positionKey(c.position);
    const person = { name: (c.name || "").trim(), company: (c.company || "").trim(), phone: (c.phone || "").trim(), email: (c.email || "").trim() };
    if (!grouped[key]) grouped[key] = { label: (c.position || "").trim() || positionLabel(key), ...person, additional: [] };
    else grouped[key].additional.push(person);
  }
  return grouped;
};
